import { useMemo, useState, type ReactNode } from 'react';
import { BrandLoading, Button, TextField } from '@scripsteam/scrips-signal-ds';
import { useOrderSets, useRestoreOrderSet } from '@/settings/services';
import { format } from 'date-fns';
import { parseNoZIsoDatetime } from '@/shared/lib/date-format';
import { showServerError, showSuccess } from '@/shared/lib/toast-helpers';
import type { OrderSetSummary } from '@/types/order-set-api';
import { OrderSetEditDialog } from './order-set-edit-dialog';
import { cardMeta, notOrderableText } from './order-set-draft';
import { ChevronDown, Pencil, Plus, Search, Undo2 } from 'lucide-react';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Provider Settings › Order sets (PROD-2710, PROD-2714; release 1: personal sets).
 *
 * A clone of the Macro tab (`../macro/macro-tab.tsx`): heading with a tertiary "+ Add order set",
 * search, one card per set with a pencil, and one Modal for adding and editing. Design: Samer's v3
 * canvas, boards Main (1), SettingsArchived (2), SettingsEmpty (3) and EditModal (4). The design is
 * the spec: copy here is the boards' own, and the classes are the boards' inline values mapped to
 * Signal DS tokens (PROD-2738, the parity pass). Board 3 draws no "Show archived" link, so the
 * link shows only when there is an archived set; board 2 draws "Hide archived" INSIDE the archived
 * section, after its cards, with no chevron.
 *
 * The caller gates the tab (`canSeeOrderSetsTab`: physician, own settings). The API scopes every
 * call to the caller, so the tab takes no provider id.
 *
 * Apply from the A/P and "Save as order set" live in the encounter (PROD-2712), not here.
 */
export function OrderSetsTab() {
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // The open modal: `null` = closed, `NEW` = "Add order set", else the set being edited.
  const [editing, setEditing] = useState<string | typeof NEW | null>(null);

  const active = useOrderSets('active');
  // Fetched up front: the toggle shows the archived count.
  const archived = useOrderSets('archived');
  const restore = useRestoreOrderSet();

  const query = search.trim();
  const activeSets = useMemo(() => filterSets(active.data ?? [], query), [active.data, query]);
  const archivedSets = useMemo(() => filterSets(archived.data ?? [], query), [archived.data, query]);
  const archivedCount = archived.data?.length ?? 0;

  const handleRestore = (set: OrderSetSummary) => {
    restore.mutate(set.id, {
      onSuccess: () => showSuccess('Order set restored'),
      onError: (err) => showServerError('order-sets.restore', err),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className={SETTINGS_SECTION_TITLE}>Order sets</h2>
        {/* Board 1: a tertiary 36px button, 12px side padding, 6px icon gap. */}
        <Button
          type="button"
          variant="tertiary"
          data-slot="settings-add"
          size="compact"
          className="gap-2 px-3 text-brand-primary"
          onClick={() => setEditing(NEW)}
        >
          <Plus className="size-4" />
          Add order set
        </Button>
      </div>

      <TextField
        size="compact"
        value={search}
        placeholder="Search order sets"
        aria-label="Search order sets"
        leadingIcon={<Search className="size-4" />}
        onChange={(e) => setSearch(e.target.value)}
      />

      {active.isLoading ? (
        <BrandLoading size={48} label="Loading order sets…" />
      ) : active.error ? (
        <p className="text-sm text-status-error">Failed to load order sets. Please try again.</p>
      ) : activeSets.length === 0 ? (
        <p className="text-sm text-text-muted">
          {query ? (
            `No order sets match “${query}”.`
          ) : (
            <>
              No order sets yet. Add one here, or build the orders in an encounter and choose{' '}
              <strong className="font-semibold text-text-secondary">Save as order set</strong> in A/P.
            </>
          )}
        </p>
      ) : (
        <ul className="space-y-2" aria-label="Order sets">
          {activeSets.map((set) => (
            <OrderSetCard key={set.id} set={set} onEdit={() => setEditing(set.id)} />
          ))}
        </ul>
      )}

      {showArchived ? (
        <section className="flex flex-col gap-2" aria-label="Archived order sets">
          <h3 className="mt-2 text-xs font-bold tracking-label uppercase text-text-muted">Archived</h3>
          <p className="text-sm text-text-muted">
            Archived sets are hidden in A/P. Visits that used them keep their orders.
          </p>
          {archived.isLoading ? (
            <BrandLoading size={32} label="Loading archived order sets…" />
          ) : archived.error ? (
            <p className="text-sm text-status-error">
              Failed to load archived order sets. Please try again.
            </p>
          ) : archivedSets.length === 0 ? (
            <p className="text-sm text-text-muted">
              {query ? `No archived order sets match “${query}”.` : 'No archived order sets.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-2" aria-label="Archived">
              {archivedSets.map((set) => (
                <OrderSetCard
                  key={set.id}
                  set={set}
                  archived
                  restoring={restore.isPending && restore.variables === set.id}
                  onRestore={() => handleRestore(set)}
                />
              ))}
            </ul>
          )}
          <ArchivedLink expanded onClick={() => setShowArchived(false)}>
            Hide archived
          </ArchivedLink>
        </section>
      ) : (
        archivedCount > 0 && (
          <ArchivedLink expanded={false} onClick={() => setShowArchived(true)}>
            Show archived ({archivedCount})
            <ChevronDown className="size-4" />
          </ArchivedLink>
        )
      )}

      {editing !== null && (
        // Mounted per open: every open starts a fresh draft.
        <OrderSetEditDialog
          key={editing}
          orderSetId={editing === NEW ? null : editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/** The modal key for "Add order set" (never a set id: ids are GUIDs). */
const NEW = 'new-order-set';

/** Name, diagnosis code or diagnosis term; case-insensitive. The API has no search. */
function filterSets(sets: OrderSetSummary[], query: string): OrderSetSummary[] {
  const q = query.toLowerCase();
  if (!q) return sets;
  return sets.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.diagnoses.some(
        (d) => d.code.toLowerCase().includes(q) || d.display.toLowerCase().includes(q),
      ),
  );
}

/**
 * "Show archived (n)" / "Hide archived" (boards 1 and 2): a plain text link, 13px semibold brand
 * blue, no box and no padding, 4px before the chevron. Not the DS Button `link` variant: that one
 * underlines, and the boards do not.
 */
function ArchivedLink({
  expanded,
  onClick,
  children,
}: {
  expanded: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onClick}
      className="self-start inline-flex items-center gap-1 p-0 border-0 bg-transparent text-sm font-semibold whitespace-nowrap text-brand-primary hover:text-interactive-hover cursor-pointer"
    >
      {children}
    </button>
  );
}

function formatDay(value: string): string {
  const d = parseNoZIsoDatetime(value);
  // "24 Sep 2026", the design's day format. date-fns, not toLocaleDateString('en-GB'): ICU
  // renders September as "Sept" there.
  return d ? format(d, 'd MMM yyyy') : '';
}

function OrderSetCard({
  set,
  archived = false,
  restoring = false,
  onEdit,
  onRestore,
}: {
  set: OrderSetSummary;
  archived?: boolean;
  restoring?: boolean;
  onEdit?: () => void;
  onRestore?: () => void;
}) {
  const day = formatDay(set.updatedDate);
  const meta = cardMeta(set, archived, day);
  const notOrderable = archived ? 0 : set.notOrderableCount ?? 0;

  return (
    <li data-slot="order-set-card" className="rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${archived ? 'text-text-muted' : 'text-text-primary'}`}>
            {set.name}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {set.diagnoses.map((d) => (
              <span
                key={`${d.system}|${d.code}`}
                title={d.display}
                data-slot="order-set-dx"
                className="inline-flex items-center h-5 px-2 rounded-control bg-surface-background text-grey-800 font-mono text-size-eyebrow tracking-wide whitespace-nowrap"
              >
                {d.code}
              </span>
            ))}
            <span className="ml-1 text-sm text-text-muted">{meta}</span>
            {notOrderable > 0 && (
              <span data-slot="order-set-not-orderable" className="inline-flex items-center whitespace-nowrap">
                {notOrderableText(notOrderable)}
              </span>
            )}
          </div>
        </div>
        {archived ? (
          // Board 2: a tertiary button at 28px with 8px side padding and a 6px icon gap.
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={onRestore}
            disabled={restoring}
            aria-label={`Restore ${set.name}`}
            className="h-8 gap-2 px-2 text-brand-primary"
          >
            <Undo2 className="size-4" />
            Restore
          </Button>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit ${set.name}`}
            className="icon-action flex-shrink-0"
          >
            <Pencil className="size-4" />
          </button>
        )}
      </div>
    </li>
  );
}
